// Words for the schedule dialog (BasePage + useFixtureForm), for every
// competition. Unit tested in tests/unit/schedule-dialog.test.ts.
//
// Rules (web tester, 2026-09-22):
//  - The title matches the button that opened the dialog (`mode`, sent by the
//    ladder challenge page); other pages derive it from the match's state.
//  - Only a save that really confirms the other side's proposal says so.
//  - A club admin who doesn't play sets the time for both sides — never
//    "confirms" anything. (The API confirms any club admin's time.)

const MODE_TITLES = {
    suggest: 'Suggest a time',
    suggestAnother: 'Suggest another time',
    change: 'Change time',
    set: 'Set a time',
    setAnother: 'Set another time',
    reschedule: 'Reschedule',
};

/**
 * @param o.mode        the opening button's mode, or null
 * @param o.isCreate    no match yet (POST fixtures/schedule)
 * @param o.isAdmin     club admin of the match's club
 * @param o.inFixture   the viewer plays in the match
 * @param o.scheduledAt the match's confirmed time, if any
 * @param o.mine        the viewer's side's open proposal
 * @param o.theirs      the other side's open proposal
 * @param o.confirms    the save would confirm the other side's proposal
 * @param o.otherName   the other side's name (players), for the note
 * @returns {{title: string, note: string, block: string|null}}
 *   block: why the save must not go ahead ("Set another time" / "Suggest
 *   another" landing on the other side's own time would confirm it — that is
 *   Confirm time's job).
 */
export function scheduleDialogCopy(o = {}) {
    const other = o.otherName || 'The other side';
    let title;
    if (o.mode && MODE_TITLES[o.mode]) title = MODE_TITLES[o.mode];
    else if (o.isCreate) title = o.isAdmin ? 'Set a time' : 'Suggest a time';
    else if (o.scheduledAt) title = 'Reschedule';
    else if (o.confirms) title = 'Confirm time';
    else if (o.isAdmin) title = 'Set a time';
    else if (o.mine) title = 'Change time';
    else if (o.theirs) title = 'Suggest another time';
    else title = 'Suggest a time';

    let note;
    let block = null;
    // "Set another time" (admin who plays) / "Suggest another" (player) answer
    // the other side's proposal with a DIFFERENT time: landing on theirs would
    // silently confirm it from a button that says otherwise — Confirm time
    // does that.
    const sameAsTheirs = `That's the time ${o.otherName || 'the other side'} suggested. Pick a different time, or use Confirm time to agree to it.`;
    if (o.mode === 'setAnother') {
        note = 'This sets the time for both sides.';
        if (o.confirms) block = sameAsTheirs;
        return {title, note, block};
    }
    if (o.mode === 'suggestAnother') {
        note = `${other} will need to confirm this time before the match is scheduled.`;
        if (o.confirms) block = sameAsTheirs;
        return {title, note, block};
    }
    if (o.isAdmin && !o.inFixture) note = 'This sets the time for both sides.';
    else if (o.confirms) note = `That's the time ${o.otherName || 'the other side'} suggested, so saving confirms it and the match is scheduled.`;
    else if (o.isAdmin) note = 'As a club admin, the time you set is confirmed straight away.';
    else note = `${other} will need to confirm this time before the match is scheduled.`;
    return {title, note, block};
}

/**
 * The toast after a schedule save, from the saved match.
 * @param saved      the API's saved fixture
 * @param o.confirms the save confirmed the other side's proposal
 * @param o.isAdmin  club admin
 * @param o.otherName the other side's name
 */
export function scheduleSavedMessage(saved, o = {}) {
    if (saved?.scheduledAt) {
        if (o.confirms) return 'Time confirmed';
        return o.isAdmin ? 'Time set' : 'Time confirmed';
    }
    if (saved?.homeTeamScheduledAt || saved?.awayTeamScheduledAt) {
        return `Time suggested, waiting for ${o.otherName || 'the other side'} to confirm`;
    }
    return 'Time saved';
}
